import React, { createContext, useContext, useState, useCallback } from 'react';

const Web3Context = createContext();

// Supported wallets config
export const WALLETS = [
  {
    id: 'metamask',
    name: 'MetaMask',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M21 3L13.5 8.5L15 5.5L21 3Z" fill="#E17726" stroke="#E17726" strokeWidth="0.25"/>
        <path d="M3 3L10.5 8.5L9 5.5L3 3Z" fill="#E27625" stroke="#E27625" strokeWidth="0.25"/>
        <path d="M18 16.5L16 19.5L20.5 20.5L21.5 16.5H18Z" fill="#E27625" stroke="#E27625" strokeWidth="0.25"/>
        <path d="M2.5 16.5L3.5 20.5L8 19.5L6 16.5H2.5Z" fill="#E27625" stroke="#E27625" strokeWidth="0.25"/>
        <path d="M7.5 11L6 13.5L10.5 13.5L10 8.5L7.5 11Z" fill="#E27625" stroke="#E27625" strokeWidth="0.25"/>
        <path d="M16.5 11L14 8.5L13.5 13.5L18 13.5L16.5 11Z" fill="#E27625" stroke="#E27625" strokeWidth="0.25"/>
        <path d="M8 19.5L10 17L8.5 16.5L8 19.5Z" fill="#E27625" stroke="#E27625" strokeWidth="0.25"/>
        <path d="M16 19.5L16 17L13.5 16.5L16 19.5Z" fill="#E27625" stroke="#E27625" strokeWidth="0.25"/>
      </svg>
    ),
    deepLink: 'https://metamask.io',
    detected: () => typeof window !== 'undefined' && !!window.ethereum?.isMetaMask,
  },
  {
    id: 'walletconnect',
    name: 'WalletConnect',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M6.1 8.5C9.3 5.4 14.5 5.4 17.7 8.5L18.1 8.9C18.3 9.1 18.3 9.4 18.1 9.6L16.9 10.8C16.8 10.9 16.6 10.9 16.5 10.8L16 10.3C13.8 8.2 10.2 8.2 8 10.3L7.5 10.8C7.4 10.9 7.2 10.9 7.1 10.8L5.9 9.6C5.7 9.4 5.7 9.1 5.9 8.9L6.1 8.5ZM20.3 11.1L21.4 12.2C21.6 12.4 21.6 12.7 21.4 12.9L16.5 17.7C16.3 17.9 16 17.9 15.8 17.7L12.4 14.3C12.35 14.25 12.27 14.25 12.22 14.3L8.2 17.7C8 17.9 7.7 17.9 7.5 17.7L2.6 12.9C2.4 12.7 2.4 12.4 2.6 12.2L3.7 11.1C3.9 10.9 4.2 10.9 4.4 11.1L8.4 14.5C8.45 14.55 8.53 14.55 8.58 14.5L11.58 11.1C11.78 10.9 12.08 10.9 12.28 11.1L15.58 14.5C15.63 14.55 15.71 14.55 15.76 14.5L19.5 11.1C19.7 10.9 20.1 10.9 20.3 11.1Z" fill="#3B99FC"/>
      </svg>
    ),
    deepLink: 'https://walletconnect.com',
    detected: () => true, // always available as QR
  },
  {
    id: 'coinbase',
    name: 'Coinbase Wallet',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="#0052FF"/>
        <path d="M12 7C9.24 7 7 9.24 7 12C7 14.76 9.24 17 12 17C14.76 17 17 14.76 17 12C17 9.24 14.76 7 12 7ZM10.5 13.5V10.5H13.5V13.5H10.5Z" fill="white"/>
      </svg>
    ),
    deepLink: 'https://www.coinbase.com/wallet',
    detected: () => typeof window !== 'undefined' && !!window.ethereum?.isCoinbaseWallet,
  },
];

// Pro NFT holder benefits
export const PRO_PERKS = {
  en: [
    'Unlimited batch downloads',
    'Priority server speed',
    'Early access to new platforms',
    'No rate limiting',
    'Download history & cloud sync',
    'Exclusive member badge',
  ],
  id: [
    'Unduhan batch tanpa batas',
    'Prioritas kecepatan server',
    'Akses awal ke platform baru',
    'Tanpa batas kecepatan',
    'Riwayat unduhan & sinkronisasi',
    'Badge member eksklusif',
  ],
};

function shortenAddress(addr) {
  return addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';
}

export function Web3Provider({ children }) {
  const [wallet, setWallet] = useState(null); // { address, ensName, balance, walletId }
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [isProHolder, setIsProHolder] = useState(false);

  const connectWallet = useCallback(async (walletId) => {
    setConnecting(true);
    setError(null);
    try {
      let address;

      if (walletId === 'metamask' && window.ethereum) {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        address = accounts[0];
      } else {
        // Simulate connection for demo / non-metamask wallets
        await new Promise(r => setTimeout(r, 1400));
        address = '0x' + Array.from({ length: 40 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
      }

      // Simulate ENS lookup
      let ensName = null;
      const knownEns = { '0x': 'vidget.eth' };
      if (Math.random() > 0.6) ensName = 'user' + Math.floor(Math.random() * 9999) + '.eth';

      // Simulate balance
      const balance = (Math.random() * 4.5 + 0.01).toFixed(4);

      // Simulate NFT/Pro check (30% chance)
      const hasPro = Math.random() > 0.7;

      setWallet({ address, ensName, balance, walletId });
      setIsProHolder(hasPro);
    } catch (err) {
      setError(err.message || 'Connection failed. Please try again.');
    } finally {
      setConnecting(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    setWallet(null);
    setIsProHolder(false);
    setError(null);
  }, []);

  const displayName = wallet
    ? (wallet.ensName || shortenAddress(wallet.address))
    : null;

  return (
    <Web3Context.Provider value={{
      wallet, connecting, error, isProHolder,
      connectWallet, disconnect, displayName,
      WALLETS,
    }}>
      {children}
    </Web3Context.Provider>
  );
}

export function useWeb3() {
  return useContext(Web3Context);
}
